type Props = {
  onLoadStale?: () => void;
  gamebook?: {
    moduleId: string;
    moduleVersion: string;
    saveRevision: number;
    sceneId: string;
    ending: string | null;
    onSave: () => void;
    onExport: () => void;
    onImport: (raw: string) => void;
    onReset: () => void;
    lastMessage?: string;
  };
};

const projectOperations = [
  ["EXPORT INVOKE PACKET", "Create a compact JSON context packet."],
  ["IMPORT DELTA", "Validate returned changes before review."],
  ["CREATE BACKUP", "Save all local DeckOS state."],
  ["RESTORE BACKUP", "Validate and restore a DeckOS backup."],
];

export function SyncScreen({ onLoadStale, gamebook }: Props) {
  return (
    <section class="sync-screen" aria-labelledby="sync-title">
      <div class="sync-heading">
        <span class="eyebrow">LOCAL EXCHANGE</span>
        <h1 id="sync-title">SYNC</h1>
      </div>

      {gamebook && (
        <>
          <div class="sync-ledger">
            {[
              ["SESSION AUTHORITY", "GAME_SESSION", "gold"],
              ["NOT A DELTA", "TRUE", "green"],
              ["NOT AN IMPRINT", "TRUE", "green"],
              ["MODULE", gamebook.moduleId, "cyan"],
              ["MODULE VER", gamebook.moduleVersion, ""],
              ["SAVE REV", String(gamebook.saveRevision), ""],
              ["SCENE", gamebook.sceneId, "cyan"],
              ["ENDING", gamebook.ending ?? "NONE", ""],
              ["AUTOSAVE", "LOCAL STORAGE", "green"],
            ].map(([label, value, tone]) => (
              <div class="sync-row" key={label}>
                <span>{label}</span>
                <strong class={tone}>{value}</strong>
              </div>
            ))}
          </div>
          <div class="sync-operations">
            <div class="section-label">GAME SESSION</div>
            <button class="sync-operation" type="button" onClick={gamebook.onSave}>
              <strong>MANUAL SAVE</strong>
              <small>Persist game session only (not project DELTA).</small>
              <span>›</span>
            </button>
            <button class="sync-operation" type="button" onClick={gamebook.onExport}>
              <strong>EXPORT SAVE</strong>
              <small>Download inert game-session JSON envelope.</small>
              <span>›</span>
            </button>
            <label class="sync-operation sync-import">
              <strong>IMPORT SAVE</strong>
              <small>Validate module/version/schema before applying.</small>
              <span>›</span>
              <input
                type="file"
                accept="application/json,.json"
                aria-label="Import game save"
                onChange={(e) => {
                  const file = (e.target as HTMLInputElement).files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => {
                    if (typeof reader.result === "string")
                      gamebook.onImport(reader.result);
                  };
                  reader.readAsText(file);
                }}
              />
            </label>
            <button class="sync-operation" type="button" onClick={gamebook.onReset}>
              <strong>RESET MODULE SESSION</strong>
              <small>Clears game-session state only. Project chips unchanged.</small>
              <span>›</span>
            </button>
            {gamebook.lastMessage && (
              <p class="meta" role="status">
                {gamebook.lastMessage}
              </p>
            )}
          </div>
        </>
      )}

      <div class="sync-ledger">
        {[
          ["LOCAL STATE", "CURRENT", "green"],
          ["FOCUS", gamebook?.sceneId?.toUpperCase() ?? "CPU-001", "cyan"],
          ["PENDING DELTAS", "0", ""],
          ["CONFLICTS", "0", ""],
          ["PROJECT IMPRINT", "UNCHANGED", "green"],
        ].map(([label, value, tone]) => (
          <div class="sync-row" key={`proj-${label}`}>
            <span>{label}</span>
            <strong class={tone}>{value}</strong>
          </div>
        ))}
      </div>
      <div class="sync-operations">
        <div class="section-label">PROJECT OPERATIONS</div>
        {projectOperations.map(([label, description]) => (
          <button class="sync-operation" key={label} type="button">
            <strong>{label}</strong>
            <small>{description}</small>
            <span>›</span>
          </button>
        ))}
        <button class="sync-share" type="button">
          SHARE PACKET
        </button>
        {onLoadStale && (
          <button class="sync-test-action" type="button" onClick={onLoadStale}>
            LOAD STALE DELTA
          </button>
        )}
      </div>
      <div class="sync-footer">
        <strong>LOCAL / CURRENT</strong>
        <span>GAME SAVE ≠ IMPRINT</span>
      </div>
    </section>
  );
}
